import React, { useState, useRef, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Bell, Check, Clock } from 'lucide-react'
import { notificationsApi } from '../../api/notifications'
import { workloadApi } from '../../api/workload'
import { useNotifStore } from '../../store/notifStore'
import { useAuthStore } from '../../store/authStore'
import { Avatar } from '../common/Avatar'
import { AttendanceWidget } from '../attendance/AttendanceWidget'
import { format } from 'date-fns'
import type { Notification } from '../../types'

function useBreadcrumb(): string {
  const location = useLocation()
  const path = location.pathname
  if (path.startsWith('/manager-home')) return 'Dashboard'
  if (path.startsWith('/dashboard-2')) return 'Dashboard 2'
  if (path.startsWith('/my-issues')) return 'My Issues'
  if (path.startsWith('/leaves')) return 'Leave Management'
  if (path.startsWith('/attendance')) return 'Attendance'
  if (path.startsWith('/project-values')) return 'Project Values'
  if (path.startsWith('/dashboard') || path.startsWith('/workload')) return 'Dashboard'
  if (path.startsWith('/people-performance')) return 'People Performance'
  if (path.startsWith('/admin/users')) return 'Admin / Users'
  if (path.startsWith('/projects') && path.includes('/settings')) return 'Project Settings'
  if (path.startsWith('/projects')) return 'Project'
  if (path.startsWith('/issues')) return 'Issue Detail'
  return 'Dashboard'
}

const todayDateInput = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const formatHours = (hours: number) => {
  if (!Number.isFinite(hours) || hours <= 0) return '0h'
  const wholeHours = Math.floor(hours)
  const minutes = Math.round((hours - wholeHours) * 60)
  if (minutes === 60) return `${wholeHours + 1}h`
  return minutes > 0 ? `${wholeHours}h ${minutes}m` : `${wholeHours}h`
}

const capacityColor = (actual: number, target: number) => {
  if (actual === 0) return '#94a3b8'
  if (actual > target) return '#ef4444'
  if (actual >= target * 0.85) return '#16a34a'
  if (actual >= target * 0.45) return '#2563eb'
  return '#f59e0b'
}

const capacityWidgetStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '9px',
  width: '250px',
  maxWidth: '32vw',
  minWidth: '190px',
  background: '#0f172a',
  color: '#ffffff',
  borderRadius: '8px',
  padding: '7px 10px',
  boxShadow: '0 8px 18px rgba(15, 23, 42, 0.16)',
}

const capacityIconStyle: React.CSSProperties = {
  width: '26px',
  height: '26px',
  borderRadius: '7px',
  background: '#2563eb',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}

const capacityTopStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '5px',
}

const capacityLabelStyle: React.CSSProperties = {
  color: '#cbd5e1',
  fontSize: '11px',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

const capacityValueStyle: React.CSSProperties = {
  color: '#ffffff',
  fontSize: '16px',
  lineHeight: 1,
  whiteSpace: 'nowrap',
}

const capacityTrackStyle: React.CSSProperties = {
  height: '5px',
  borderRadius: '999px',
  background: 'rgba(255,255,255,0.16)',
  overflow: 'hidden',
}

const capacityFillStyle: React.CSSProperties = {
  height: '100%',
  borderRadius: '999px',
  transition: 'width 0.2s ease',
}

export const Header: React.FC = () => {
  const navigate = useNavigate()
  const breadcrumb = useBreadcrumb()
  const { user } = useAuthStore()
  const { unreadCount, setUnreadCount, decrementUnread } = useNotifStore()
  const [notifOpen, setNotifOpen] = useState(false)
  const notifRef = useRef<HTMLDivElement>(null)
  const qc = useQueryClient()
  const today = todayDateInput()

  const { data: unreadData } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationsApi.getUnreadCount(),
    enabled: !!user,
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000,
  })

  const { data: capacityData } = useQuery({
    queryKey: ['header-capacity', user?.id, today],
    queryFn: () => workloadApi.getDashboard({ startDate: today, endDate: today, employeeId: user?.id }),
    enabled: !!user && user.role !== 'CLIENT',
    staleTime: 30 * 1000,
    refetchInterval: 5 * 60 * 1000,
  })

  const { data: notifsData } = useQuery({
    queryKey: ['notifications', 'recent'],
    queryFn: () => notificationsApi.listNotifications({ limit: 10 }),
    enabled: notifOpen,
  })

  const markAllMutation = useMutation({
    mutationFn: () => notificationsApi.markAllRead(),
    onSuccess: () => {
      setUnreadCount(0)
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  const markReadMutation = useMutation({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
      qc.invalidateQueries({ queryKey: ['notifications', 'unread-count'] })
    },
  })

  useEffect(() => {
    if (!notifOpen) return
    const handler = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node)) {
        setNotifOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [notifOpen])

  useEffect(() => {
    if (typeof unreadData?.data?.data?.count === 'number') {
      setUnreadCount(unreadData.data.data.count)
    }
  }, [unreadData, setUnreadCount])

  const notifs = notifsData?.data?.data || []
  const visibleNotifs = notifs.filter((notification) => !notification.isRead)
  const capacityReview = capacityData?.data?.data
  const currentMember = capacityReview?.daily?.[0]?.members?.find((member) => member.user.id === user?.id)
  const actualHours = currentMember?.actualHours || 0
  const targetHours = currentMember?.targetHours || capacityReview?.targetHoursPerMember || 8
  const capacityPercent = Math.min((actualHours / Math.max(targetHours, 1)) * 100, 100)
  const showCapacity = !!user && user.role !== 'CLIENT'

  const openNotification = (notification: Notification) => {
    if (!notification.isRead) {
      markReadMutation.mutate(notification.id)
      decrementUnread()
    }
    setNotifOpen(false)
    navigate(`/issues/${notification.issue.id}`)
  }

  return (
    <div
      style={{
        height: '52px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        flexShrink: 0,
        zIndex: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span style={{ fontSize: '14px', fontWeight: 600, color: '#1a202c' }}>{breadcrumb}</span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {showCapacity && <AttendanceWidget />}

        {showCapacity && (
          <div style={capacityWidgetStyle}>
            <div style={capacityIconStyle}>
              <Clock size={13} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={capacityTopStyle}>
                <span style={capacityLabelStyle}>Task logs today</span>
                <strong style={capacityValueStyle}>{formatHours(actualHours)} / {formatHours(targetHours)}</strong>
              </div>
              <div style={capacityTrackStyle}>
                <div
                  style={{
                    ...capacityFillStyle,
                    width: `${capacityPercent}%`,
                    background: capacityColor(actualHours, targetHours),
                  }}
                />
              </div>
            </div>
          </div>
        )}

        {/* Notification Bell */}
        <div ref={notifRef} style={{ position: 'relative' }}>
          <button
            onClick={() => setNotifOpen((v) => !v)}
            style={{
              background: notifOpen ? '#f1f5f9' : 'none',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '6px',
              borderRadius: '8px',
              position: 'relative',
              transition: 'background 0.15s',
              color: '#64748b',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = '#f1f5f9')}
            onMouseLeave={(e) => (e.currentTarget.style.background = notifOpen ? '#f1f5f9' : 'none')}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span
                style={{
                  position: 'absolute',
                  top: '3px',
                  right: '3px',
                  width: '16px',
                  height: '16px',
                  background: '#ef4444',
                  borderRadius: '50%',
                  fontSize: '9px',
                  fontWeight: 700,
                  color: '#fff',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1,
                }}
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {notifOpen && (
            <div
              style={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: 0,
                width: '360px',
                background: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: '10px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                zIndex: 1000,
                overflow: 'hidden',
                animation: 'dropIn 0.12s ease',
              }}
            >
              <style>{`@keyframes dropIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }`}</style>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '12px 16px',
                  borderBottom: '1px solid #e2e8f0',
                }}
              >
                <span style={{ fontWeight: 600, fontSize: '14px', color: '#1a202c' }}>
                  Notifications
                </span>
                {unreadCount > 0 && (
                  <button
                    onClick={() => markAllMutation.mutate()}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      color: '#6366f1',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontWeight: 500,
                    }}
                  >
                    <Check size={12} /> Mark all read
                  </button>
                )}
              </div>
              <div style={{ maxHeight: '380px', overflowY: 'auto' }}>
                {visibleNotifs.length === 0 ? (
                  <p style={{ padding: '24px', textAlign: 'center', color: '#718096', fontSize: '13px' }}>
                    No notifications yet
                  </p>
                ) : (
                  visibleNotifs.map((n) => (
                    <div
                      key={n.id}
                      onClick={() => openNotification(n)}
                      style={{
                        display: 'flex',
                        gap: '10px',
                        padding: '12px 16px',
                        background: '#f0f4ff',
                        borderBottom: '1px solid #f1f5f9',
                        cursor: 'pointer',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = '#f0f4ff')}
                    >
                      <Avatar user={n.actor} size="sm" style={{ flexShrink: 0, marginTop: '2px' }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        {n.type === 'ISSUE_ASSIGNED' ? (
                          <>
                            <p style={{ fontSize: '12px', color: '#4f46e5', margin: 0, lineHeight: '1.4', fontWeight: 700 }}>
                              {n.issue.project?.name || 'Project'} · {n.issue.identifier}
                            </p>
                            <p style={{ fontSize: '13px', color: '#0f172a', margin: '2px 0 0', lineHeight: '1.45', fontWeight: 600 }}>
                              {n.issue.title}
                            </p>
                            <p style={{ fontSize: '11px', color: '#64748b', margin: '4px 0 0' }}>
                              Priority: {n.issue.priority.replace(/_/g, ' ')}
                            </p>
                          </>
                        ) : (
                          <p style={{ fontSize: '13px', color: '#374151', margin: 0, lineHeight: '1.4' }}>
                            {n.message}
                          </p>
                        )}
                        <p style={{ fontSize: '11px', color: '#94a3b8', margin: '3px 0 0' }}>
                          {format(new Date(n.createdAt), 'MMM d, yyyy')}
                        </p>
                      </div>
                      <div
                        style={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          background: '#6366f1',
                          flexShrink: 0,
                          marginTop: '6px',
                        }}
                      />
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
